import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, Package, Layers, MapPin, Clock, TrendingUp, Zap, ChevronRight } from "lucide-react";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const SRC_LABEL = { manual: "Manuale", scheduled: "Programmazione", bulk: "Azione massiva", import: "Import", undo: "Ripristino" };

export default function AdminDashboardLive() {
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(new Date());
  const timer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/dashboard/live");
      setData(r.data); setTs(new Date());
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    timer.current = setInterval(load, 8000);
    return () => clearInterval(timer.current);
  }, []);

  if (!data) return <div className="p-8 text-ink-500">Caricamento dashboard…</div>;

  const p = data.products, c = data.categories, at = data.auto_today, st = data.stations;

  return (
    <div data-testid="admin-dashboard-live">
      <div className="eyebrow flex items-center gap-2"><Activity className="w-3 h-3 animate-pulse text-emerald-600" /> LIVE</div>
      <h1 className="text-4xl mt-1">Dashboard Operativa</h1>
      <p className="text-sm text-ink-500 mt-1">Aggiornamento automatico ogni 8 secondi · ultimo refresh <b>{ts.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</b></p>

      <div className="grid gap-3 mt-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-prod-avail" icon={<Package className="w-5 h-5" />} label="Prodotti disponibili" value={p.available} total={p.total} color="emerald" />
        <StatCard testid="stat-prod-unavail" icon={<Package className="w-5 h-5" />} label="Prodotti esauriti" value={p.unavailable} total={p.total} color="red" />
        <StatCard testid="stat-cat-visible" icon={<Layers className="w-5 h-5" />} label="Categorie attive" value={c.visible} total={c.total} color="emerald" />
        <StatCard testid="stat-cat-hidden" icon={<Layers className="w-5 h-5" />} label="Categorie nascoste" value={c.hidden} total={c.total} color="amber" />
      </div>

      <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-prod-scheduled" icon={<Clock className="w-5 h-5" />} label="Prodotti programmati" value={p.scheduled} color="bronze" />
        <StatCard testid="stat-auto-today" icon={<Zap className="w-5 h-5" />} label="Cambi automatici previsti oggi" value={at.products + at.categories} color="bronze" />
        <StatCard testid="stat-stations" icon={<MapPin className="w-5 h-5" />} label="Postazioni attive" value={st.active} total={st.total} color="ink" />
        <Link to="/admin/activity" className="rounded-2xl border-2 border-bronze-500 bg-bronze-50 text-bronze-800 p-4 flex items-center justify-between hover:shadow-md transition-shadow" data-testid="link-activity">
          <div>
            <div className="text-xs uppercase font-bold">Cronologia</div>
            <div className="text-2xl font-serif mt-1">Timeline →</div>
          </div>
          <ChevronRight className="w-6 h-6" />
        </Link>
      </div>

      <div className="card-elevated p-4 mt-6" data-testid="recent-changes">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-serif text-2xl">Ultimi cambi disponibilità</h2>
          <Link to="/admin/activity" className="text-xs text-bronze-600 hover:underline">Vedi tutto →</Link>
        </div>
        {data.recent_changes.length === 0 ? (
          <div className="text-ink-500 text-sm">Nessuna modifica recente.</div>
        ) : (
          <ol className="divide-y divide-sand-300">
            {data.recent_changes.map((r) => (
              <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`recent-${r.id}`}>
                <div className="text-xs text-ink-500 w-14 shrink-0 font-mono">{fmtTime(r.at)}</div>
                <div className={`w-2 h-2 rounded-full shrink-0 ${r.to_state ? "bg-emerald-500" : "bg-red-500"}`} />
                <div className="min-w-0 flex-1 text-sm">
                  <b>{r.product_name}</b>
                  <span className="mx-1.5 text-ink-500">·</span>
                  <span className={r.from_state ? "text-emerald-700" : "text-red-700"}>{r.from_state ? "Disponibile" : "Esaurito"}</span>
                  <span className="mx-1 text-ink-500">→</span>
                  <span className={r.to_state ? "text-emerald-700" : "text-red-700"}>{r.to_state ? "Disponibile" : "Esaurito"}</span>
                </div>
                <div className="text-xs text-ink-500 shrink-0 truncate max-w-[120px]">{r.user_name}</div>
                <span className="chip text-[10px] bg-sand-100 border-sand-300 shrink-0">{SRC_LABEL[r.source] || r.source}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, icon, label, value, total, color }) {
  const palette = {
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-800",
    red: "border-red-500 bg-red-50 text-red-800",
    amber: "border-amber-500 bg-amber-50 text-amber-800",
    bronze: "border-bronze-500 bg-sand-50 text-bronze-800",
    ink: "border-ink-500 bg-white text-ink-800",
  }[color] || "border-sand-500 bg-white";
  return (
    <div className={`rounded-2xl border-2 p-4 ${palette}`} data-testid={testid}>
      <div className="flex items-center gap-2 opacity-80">{icon}<div className="text-xs uppercase font-bold">{label}</div></div>
      <div className="mt-2 flex items-baseline gap-1">
        <div className="text-4xl font-serif">{value}</div>
        {total !== undefined && <div className="text-sm text-ink-500">/ {total}</div>}
      </div>
    </div>
  );
}
